import { Form, Spin } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useContext, useEffect, useState } from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { NewPackModal } from 'app/components/pages/Packs/NewPackModal/NewPackModal';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useSortHotelEntities } from 'app/hooks/data/core/admin/hotels/useSortHotelEntities';

import styles from './PackagesRoomsAmenities.module.scss';
import { ActivitiesTable } from './_components/ActivitiesTable';
import { PackagesTable } from './_components/PackagesTable';
import { RoomsTable } from './_components/RoomsTable';
import { groupPackagesByPeriod } from './_components/_utils/packageUtils';
import {
  withAlwaysIncludedActivityPriority,
  withRoomPriority,
} from './_components/_utils/priorityUtils';
import { useActivityActions } from './_hooks/useActivityActions';
import { useEntityNavigation } from './_hooks/useEntityNavigation';
import { useEntityReorder } from './_hooks/useEntityReorder';
import { useHotelEntitiesData } from './_hooks/useHotelEntitiesData';
import { usePackageActions } from './_hooks/usePackageActions';
import { usePublishError } from './_hooks/usePublishError';
import { useRoomActions } from './_hooks/useRoomActions';
import type { PackagesRoomsAmenitiesFormValues } from './_types';

type Props = {
  headerMenu: React.ReactNode;
  headerExtra?: React.ReactNode[];
  hotelId: number;
};

const PackagesRoomsAmenitiesContent = ({
  headerMenu,
  headerExtra,
  hotelId,
}: Props) => {
  const [form] = Form.useForm<PackagesRoomsAmenitiesFormValues>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { mode, enterEditMode, enterViewMode, isEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: sortHotelEntities } = useSortHotelEntities({ hotelId });

  // --- Data ---
  const {
    hotel,
    isFetchHotelLoading,
    packages: fetchedPackages,
    rooms: fetchedRooms,
    activities: fetchedActivities,
    refetchPackages,
    refetchRooms,
    refetchActivities,
    isFetchEntitiesLoading,
  } = useHotelEntitiesData(hotelId);

  const packages =
    Form.useWatch<typeof fetchedPackages>('packages', form) ?? fetchedPackages;
  const rooms =
    Form.useWatch<typeof fetchedRooms>('rooms', form) ?? fetchedRooms;
  const activities =
    Form.useWatch<typeof fetchedActivities>('activities', form) ??
    fetchedActivities;

  useEffect(() => {
    form.setFieldsValue({
      packages: fetchedPackages,
      rooms: withRoomPriority(fetchedRooms),
      activities: withAlwaysIncludedActivityPriority(fetchedActivities),
    });
  }, [fetchedPackages, fetchedRooms, fetchedActivities, form]);

  // --- Publish error ---
  const {
    publishErrorType,
    missingFieldsError,
    openPublishErrorModal,
    closePublishErrorModal,
  } = usePublishError();

  // --- Actions ---
  const { publishPackage, setupPackage, unpublishPackage, hidePackage } =
    usePackageActions({
      refetchPackages,
      openPublishErrorModal,
      isLegacy: hotel?.isLegacy ?? true,
    });

  const { publishRoom, unpublishRoom } = useRoomActions({
    hotelId,
    refetchRooms,
    openPublishErrorModal,
    isLegacy: hotel?.isLegacy ?? true,
  });

  const { publishActivity, unpublishActivity } = useActivityActions({
    hotelId,
    refetchActivities,
    openPublishErrorModal,
    isLegacy: hotel?.isLegacy ?? true,
  });

  // --- Navigation ---
  const { goToCreateRoom, goToCreateActivity } = useEntityNavigation({
    hotelId,
    isLegacy: hotel?.isLegacy ?? false,
  });

  // --- Reorder ---
  const { handleReorderPackages, handleReorderRooms, handleReorderActivities } =
    useEntityReorder({ form, packages, activities });

  // --- Save ---
  const handleSave = async () => {
    const values = await form.validateFields();

    await sortHotelEntities({
      rooms: values.rooms.map(({ id, hotelPriority }) => ({
        id,
        hotelPriority: hotelPriority ?? 0,
      })),
      alwaysIncludedExperiences: values.activities
        .filter((activity) => activity.isAlwaysIncludedWithNight)
        .map(({ id, hotelPriority }) => ({
          id,
          hotelPriority: hotelPriority ?? 0,
        })),
    });

    enterViewMode();
  };

  // --- Derived state ---
  const { daytimePackages, nightStayPackages } =
    groupPackagesByPeriod(packages);

  if (isFetchHotelLoading) {
    return null;
  }

  const isLegacy = hotel?.isLegacy ?? true;

  return (
    <>
      <Form form={form} initialValues={{ packages, rooms, activities }}>
        <SDetailLayout
          title={
            <AssetDetailTitle
              assetTitle={hotel?.name ?? `Hôtel #${hotelId}`}
              hotelName={hotel?.name}
              isNewDataModel={!hotel?.isLegacy}
            />
          }
          backRoute="/hotels"
          mode={mode}
          onEdit={enterEditMode}
          onCancel={enterViewMode}
          onSave={handleSave}
          headerMenu={headerMenu}
          headerExtra={headerExtra}
          isDirty={true}
        >
          <Spin
            spinning={isFetchEntitiesLoading}
            wrapperClassName={styles.mainContent}
          >
            <Content className={`hotel-detail__content ${styles.content}`}>
              <Form.Item name="packages" hidden>
                <input />
              </Form.Item>
              <Form.Item name="rooms" hidden>
                <input />
              </Form.Item>
              <Form.Item name="activities" hidden>
                <input />
              </Form.Item>

              <PackagesTable
                daytimePackages={daytimePackages}
                nightStayPackages={nightStayPackages}
                isLegacy={isLegacy}
                onCreatePackage={() => setIsCreateModalOpen(true)}
                onPublish={publishPackage}
                onSetup={setupPackage}
                onUnpublish={unpublishPackage}
                onHide={hidePackage}
                onReorder={isEditMode ? handleReorderPackages : undefined}
                activities={activities}
                rooms={rooms}
              />

              <ActivitiesTable
                hotelId={hotelId}
                activities={activities}
                onCreateActivity={goToCreateActivity}
                onPublish={publishActivity}
                onUnpublish={unpublishActivity}
                onReorder={isEditMode ? handleReorderActivities : undefined}
                isLegacy={isLegacy}
              />

              <RoomsTable
                rooms={rooms}
                isLegacy={isLegacy}
                onCreateRoom={goToCreateRoom}
                onPublish={publishRoom}
                onUnpublish={unpublishRoom}
                onReorder={isEditMode ? handleReorderRooms : undefined}
              />
            </Content>
          </Spin>
        </SDetailLayout>
      </Form>

      <NewPackModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        hotelId={hotelId}
      />

      <PublishModalError
        type={publishErrorType ?? PUBLISH_ERROR_TYPE.PACKAGE}
        missingFields={missingFieldsError ?? []}
        isOpen={publishErrorType !== null}
        handleOk={closePublishErrorModal}
      />
    </>
  );
};

export const PackagesRoomsAmenities = (props: Props) => {
  return (
    <ViewEditModeContextProvider>
      <PackagesRoomsAmenitiesContent {...props} />
    </ViewEditModeContextProvider>
  );
};
